import { FC, useEffect, useRef, useState } from 'react';
import {
    Typography,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Theme,
    IconButton,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { PauseCircleFilled, PlayCircleFilled, RemoveCircle } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRecoilState, useRecoilValue } from 'recoil';
import orderState, { currentEntityState, filesState } from 'TaxAnalyticsState/orderState';
import { FlowChunk, FlowFile } from 'flowjs';
import { flow, SubscribeFlowEvents, ToggleResumePause, RemoveFile, fileStatuses, ToFileDTO, getFileStatus, fileInterruped } from 'TaxAnalyticsHelpers/Upload';
import { FileDTO, FileStatus, FileType, FlowFileUpload } from 'TaxAnalyticsHelpers/TaxAnalyticsClient';
import { DeloitteProgress } from 'TaxAnalyticsUI/Deloitte';
import { PrettyBytes } from 'TaxAnalyticsHelpers/Numbers';

const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
        head: {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
        },
        body: {
            fontSize: 14,
        },
    }),
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
    createStyles({
        root: {
            '&:nth-of-type(odd)': {
                backgroundColor: theme.palette.action.hover,
            },
        },
    }),
)(TableRow);

export const OrderUploads: FC = (props: any) => {
    const order = useRecoilValue(orderState);
    const currentEntity = useRecoilValue(currentEntityState);
    const [files, setFiles] = useRecoilState(filesState);
    const [selectedFileSettingId, setSelectedFileSettingId] = useState<string>();
    const inputFile = useRef<HTMLInputElement | null>(null);
    const filesAdded = (addedFiles: FlowFileUpload[]) => {
        const uFiles: FileDTO[] = [];
        files?.forEach(f => {
            uFiles.push(f);
        });
        addedFiles.forEach(file => {
            const newFile = ToFileDTO(file, FileType.Input, FileStatus.Added);
            var index = uFiles.findIndex(f => f.id === newFile.id);
            if (index > -1) {
                uFiles.splice(index, 1, newFile);
            }
            else {
                uFiles.push(newFile);
            }
        });
        setFiles(uFiles);
    };

    const fileProgress = (file: FileDTO) => {
        const uFiles: FileDTO[] = [];
        files?.forEach(f => {
            uFiles.push(f);
        });
        //if (files?.length === 0)
        //    uFiles.push(file);
        var index = files?.findIndex(f => f.uniqueIdentifier === file.uniqueIdentifier) ?? -1;
        if (index > -1) {
            var currentFile = files![index];
            file.id = currentFile.id;
            uFiles.splice(index, 1, file);
        }
        if (file.paused)
            file.status = 2;
        if (file.isUploading)
            file.status = 1;
        setFiles(uFiles);
    };

    const removeFileCallback = (file: FileDTO) => {
        const uFiles: FileDTO[] = [];
        files?.forEach(f => {
            if (f.id !== file.id)
                uFiles.push(f);
        });
        setFiles(uFiles);
    }

    const onInputChange = (event: any) => {
        event.fileSettingId = selectedFileSettingId;
        (flow as any).addFiles(event.target.files, event);
    }

    const reuploadCallback = (file: FileDTO) => {
        setSelectedFileSettingId(file.fileSettingId);
        inputFile?.current?.click();
    }

    useEffect(() => {
        flow.opts.query = (file: FlowFile, chunk: FlowChunk, isTest: boolean) => {
            if ((file as any).fileSettingId)
                return {
                    id: (file as any).id,
                    fileContainerId: currentEntity?.fileContainerId,
                    entityId: order!.entityId,
                    orderId: order!.id,
                    fileSettingId: (file as any).fileSettingId
                }
            else {
                return {
                    id: (file as any).id,
                    fileContainerId: currentEntity?.fileContainerId,
                    entityId: order!.entityId,
                    orderId: order!.id
                }
            }
        }
        return SubscribeFlowEvents("dropInput", FileType.Input, filesAdded, fileProgress);
    });

    return (
        <Paper elevation={0}>
            <Grid container spacing={6} id="dropInput" direction="row" justifyContent="center" alignItems="stretch">
                <Grid xs={12} md={12} container alignItems="center" justifyContent="center">
                    <Grid container md={8} xs={12} direction="row" spacing={2}>
                        <Grid>
                            <Typography variant="h4" flexShrink={1}>Fichiers</Typography>
                        </Grid>
                        <Grid>
                            <IconButton
                                aria-label="upload"
                                id='browseFile'
                                onClick={(event) => { setSelectedFileSettingId(""); inputFile?.current?.click(); }}
                                size="large">
                                <CloudUploadIcon />
                            </IconButton>
                        </Grid>
                        <input name="uploadButton" type="file" multiple ref={inputFile} style={{ display: 'none' }} onChange={(event) => { onInputChange(event); }} />
                    </Grid>
                </Grid>
                <Grid xs={12} md={12} container alignItems="center" justifyContent="center">
                    <Grid md={8} xs={12}>
                        <TableContainer component={Paper} elevation={0}>
                            <Table aria-label="simple table">
                                <TableHead >
                                    <StyledTableRow>
                                        <StyledTableCell>Type</StyledTableCell>
                                        <StyledTableCell>Nom</StyledTableCell>
                                        <StyledTableCell>Taille</StyledTableCell>
                                        <StyledTableCell align="center">Transfers</StyledTableCell>
                                        <StyledTableCell align="center">Status</StyledTableCell>
                                        <StyledTableCell align="right">Actions</StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        order && order?.orderFileSettings?.map((fileSetting, index) => {
                                            if (files?.findIndex(f => f.fileSettingId === fileSetting.id) === -1)
                                                return (
                                                    <StyledTableRow key={index}>
                                                        <StyledTableCell component="th" scope="row">
                                                            {fileSetting.setting?.translations![0].description}
                                                        </StyledTableCell>
                                                        <StyledTableCell></StyledTableCell>
                                                        <StyledTableCell></StyledTableCell>
                                                        <StyledTableCell align="center"></StyledTableCell>
                                                        <StyledTableCell align="center"></StyledTableCell>
                                                        <StyledTableCell align="right">
                                                            <IconButton
                                                                aria-label="upload"
                                                                id='browseFile'
                                                                onClick={() => { setSelectedFileSettingId(fileSetting.id); inputFile?.current?.click() }}
                                                                size="large">
                                                                <CloudUploadIcon />
                                                            </IconButton>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                );
                                            return;
                                        })
                                    }
                                    {files?.map((file, index) => {
                                        return (
                                            <StyledTableRow key={index}>
                                                <StyledTableCell component="th" scope="row">
                                                    {order && order?.orderFileSettings?.find(fs => fs.id === file.fileSettingId)?.setting?.translations![0].description}
                                                </StyledTableCell>
                                                <StyledTableCell>{file.name}</StyledTableCell>
                                                <StyledTableCell>{PrettyBytes(file.size, {})}</StyledTableCell>
                                                <StyledTableCell align="center"><DeloitteProgress value={file.progress ?? 0 * 100} variant={((file.progress ?? 0 * 100 === 100) || fileInterruped(file)) ? 'determinate' : undefined} /></StyledTableCell>
                                                <StyledTableCell align="center">{getFileStatus(file)}</StyledTableCell>
                                                <StyledTableCell align="right">
                                                    {file.isUploading &&
                                                        <IconButton
                                                            aria-label="pause"
                                                            onClick={() => ToggleResumePause(file, fileProgress)}
                                                            size="large">
                                                            <PauseCircleFilled />
                                                        </IconButton>
                                                    }
                                                    {(file.paused || fileInterruped(file)) &&
                                                        <IconButton
                                                            aria-label="resume"
                                                            onClick={() => ToggleResumePause(file, fileProgress, reuploadCallback)}
                                                            size="large">
                                                            <PlayCircleFilled />
                                                        </IconButton>
                                                    }
                                                    {(file.status === FileStatus.Completed || file.status === FileStatus.Paused) &&
                                                        <IconButton
                                                            aria-label="delete"
                                                            onClick={() => { RemoveFile(file, removeFileCallback) }}
                                                            size="large">
                                                            <RemoveCircle />
                                                        </IconButton>
                                                    }
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Grid >
        </Paper >
    );
}